import { CertificationRecord } from '__generated__/graphql';
import { CERTIFICATION_RECORD_FRAGMENT } from 'apollo/fragments/certification-record-fragments';
import gql from 'graphql-tag';
import { useApolloPaging } from 'hooks';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import ICursorable from 'types/Cursorable';
import {
  CertificationsExpirationOptions,
  ICertificationStatusReportFilterData,
} from '../CertificationStatusReportFilter/CertificationStatusReportFilter';
import { CertificationStatusReportTabType } from '../CertificationStatusReportTabHeader/CertificationStatusReportTabHeader';
import { mapCertificationRecordToTableData } from '../utils/utils';

export function useCertificationStatusReportData() {
  const { getAll } = useApolloPaging();

  return useCallback(async (filterData: ICertificationStatusReportFilterData) => {
    const beginningOfDay = DateTime.now().startOf('day').toISODate();

    let expiresOnFilter: any;
    switch (filterData.reportType) {
      case CertificationStatusReportTabType.ACTIVE:
        if (!isNil(filterData.expirationDate)) {
          switch (filterData.expirationDate) {
            case CertificationsExpirationOptions.SEVEN_DAYS:
              expiresOnFilter = {
                greaterThanOrEqual: DateTime.now().toISODate(),
                lessThan: DateTime.now().plus({ days: 7 }).toISODate(),
              };
              break;
            case CertificationsExpirationOptions.THIRTY_DAYS:
              expiresOnFilter = {
                greaterThanOrEqual: DateTime.now().toISODate(),
                lessThan: DateTime.now().plus({ days: 30 }).toISODate(),
              };
              break;
            case CertificationsExpirationOptions.SIXTY_DAYS:
              expiresOnFilter = {
                greaterThanOrEqual: DateTime.now().toISODate(),
                lessThan: DateTime.now().plus({ days: 60 }).toISODate(),
              };
              break;
            case CertificationsExpirationOptions.NINETY_DAYS:
              expiresOnFilter = {
                greaterThanOrEqual: DateTime.now().toISODate(),
                lessThan: DateTime.now().plus({ days: 90 }).toISODate(),
              };
              break;
            case CertificationsExpirationOptions.NONE:
              expiresOnFilter = { isNull: true };
              break;
          }
        }
        break;
      case CertificationStatusReportTabType.EXPIRED:
        expiresOnFilter = { lessThan: beginningOfDay };
        break;
    }

    const getCertificationRecords = getAll<CertificationRecord & ICursorable>('certificationRecords', {
      query: CERTIFICATION_RECORDS_QUERY,
      variables: {
        first: 500,
        filter: {
          memberId: filterData.memberId ? { equal: filterData.memberId } : undefined,
          member: filterData.memberGroupId ? { memberGroupId: { equal: filterData.memberGroupId } } : undefined,
          certificationId: filterData.certificationId ? { equal: filterData.certificationId } : undefined,
          deletedOn: { isNull: true },
          expiresOn: expiresOnFilter,
          certification: {
            deletedOn: { isNull: true },
          },
        },
      },
      fetchPolicy: 'network-only',
    });
    return await Promise.all([getCertificationRecords]).then((queryResults) => {
      const [results] = queryResults;
      return mapCertificationRecordToTableData(results);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export const CERTIFICATION_RECORDS_QUERY = gql`
  query CertificationRecords($first: Int, $filter: CertificationRecordFilter, $after: String) {
    certificationRecords(first: $first, filter: $filter, after: $after) {
      ...CertificationRecord
      cursor
    }
  }
  ${CERTIFICATION_RECORD_FRAGMENT}
`;
